/**
 * Dispatch engine API (WP-P2). Every call here matches a real endpoint
 * shipped by WP-B — see `docs/revamp/changelog_WP-B.md` §3 (inbox), §4
 * (today work list, handover, auto-plan control).
 *
 * `withFallback` is used only where a *missing* endpoint must not break the
 * page (Today work list, inbox listing); mutations propagate their errors so
 * the caller can toast them.
 */
import { api, API_PREFIX, withFallback } from './client'
import type {
  AutoPlanRun,
  BulkApproveResult,
  DispatchInboxPage,
  DispatchInboxSummary,
  DispatchTask,
  DispatchTodayResponse,
  HandoverNote,
  TaskAction,
  TaskActionResult,
  TodayWindowKind,
} from '../../types/dispatch'

const EMPTY_TODAY = (date: string, kind: TodayWindowKind): DispatchTodayResponse => ({
  date,
  window: { start: `${date}T00:00:00Z`, end: `${date}T23:59:59Z`, timezone: 'UTC', kind },
  summary: { due: 0, uncovered: 0, proposed: 0, awaiting_acceptance: 0, in_progress: 0, completed: 0, exceptions_open: 0 },
  rows: [],
})

export interface DispatchTodayParams {
  date?: string
  group_id?: string
  window?: TodayWindowKind
}

/** GET /dispatch/today?date=&group_id=&window=day|shift */
export async function getDispatchToday(params: DispatchTodayParams = {}): Promise<DispatchTodayResponse> {
  const date = params.date ?? new Date().toISOString().slice(0, 10)
  const kind: TodayWindowKind = params.window ?? 'day'
  return withFallback(async () => {
    const res = await api.get<DispatchTodayResponse>(`${API_PREFIX}/dispatch/today`, {
      params: { date: params.date, group_id: params.group_id || undefined, window: kind },
    })
    return res.data ?? EMPTY_TODAY(date, kind)
  }, EMPTY_TODAY(date, kind))
}

export interface DispatchInboxParams {
  /** `open` (default) · `closed` · `all` · comma list of states. */
  state?: string
  /** comma list of task types */
  type?: string
  /** user id · `me` · `unassigned` */
  owner?: string
  severity?: string
  date?: string
  group_id?: string
  load_id?: string
  page?: number
  page_size?: number
}

/** GET /dispatch/inbox */
export async function getDispatchInbox(params: DispatchInboxParams = {}): Promise<DispatchInboxPage> {
  const empty: DispatchInboxPage = { items: [], total: 0, page: params.page ?? 1, page_size: params.page_size ?? 50 }
  return withFallback(async () => {
    const res = await api.get<DispatchInboxPage>(`${API_PREFIX}/dispatch/inbox`, { params: clean(params) })
    return res.data ?? empty
  }, empty)
}

/** GET /dispatch/inbox/summary */
export async function getInboxSummary(groupId?: string): Promise<DispatchInboxSummary> {
  const empty: DispatchInboxSummary = {
    total_open: 0, open: 0, awaiting_approval: 0, snoozed: 0, overdue: 0, mine: 0, by_type: {}, by_severity: {},
  }
  return withFallback(async () => {
    const res = await api.get<DispatchInboxSummary>(`${API_PREFIX}/dispatch/inbox/summary`, {
      params: groupId ? { group_id: groupId } : undefined,
    })
    return res.data ?? empty
  }, empty)
}

/** GET /dispatch/inbox/{id} — task + its load snapshot. */
export async function getInboxTask(id: string): Promise<DispatchTask> {
  const res = await api.get<DispatchTask>(`${API_PREFIX}/dispatch/inbox/${id}`)
  return res.data
}

/** POST /dispatch/inbox/{id}/action */
export async function actOnTask(
  id: string,
  action: TaskAction | string,
  payload?: Record<string, unknown>,
): Promise<TaskActionResult> {
  const res = await api.post<TaskActionResult>(`${API_PREFIX}/dispatch/inbox/${id}/action`, { action, payload: payload ?? {} })
  return res.data
}

/** POST /dispatch/inbox/bulk-approve — `{ids}` or `{date, group_id}`. */
export async function bulkApproveTasks(
  body: { ids: string[] } | { date: string; group_id?: string },
): Promise<BulkApproveResult> {
  const res = await api.post<BulkApproveResult>(`${API_PREFIX}/dispatch/inbox/bulk-approve`, body)
  return res.data
}

/** GET /dispatch/handover?date=&group_id= */
export async function getHandover(date?: string, groupId?: string): Promise<HandoverNote> {
  const fallback: HandoverNote = {
    date: date ?? new Date().toISOString().slice(0, 10),
    group_id: groupId ?? null,
    generated: null,
    generated_text: '',
    text: null,
    updated_by: null,
    updated_at: null,
  }
  return withFallback(async () => {
    const res = await api.get<HandoverNote>(`${API_PREFIX}/dispatch/handover`, {
      params: clean({ date, group_id: groupId }),
    })
    return res.data ?? fallback
  }, fallback)
}

/** PUT /dispatch/handover */
export async function saveHandover(body: { date?: string; group_id?: string; text: string }) {
  const res = await api.put(`${API_PREFIX}/dispatch/handover`, clean(body))
  return res.data as { id: string; date: string; group_id: string | null; text: string; updated_by: string | null; updated_at: string }
}

/** POST /dispatch/auto-plan/run — 409 when the account lock is held. */
export async function runAutoPlan(body: { dry_run?: boolean; group_id?: string } = {}): Promise<AutoPlanRun> {
  const res = await api.post<AutoPlanRun>(`${API_PREFIX}/dispatch/auto-plan/run`, {
    dry_run: body.dry_run ?? false,
    ...(body.group_id ? { group_id: body.group_id } : {}),
  })
  return res.data
}

/** GET /dispatch/auto-plan/runs?limit= */
export async function getAutoPlanRuns(limit = 10): Promise<AutoPlanRun[]> {
  return withFallback(async () => {
    const res = await api.get<{ items: AutoPlanRun[] }>(`${API_PREFIX}/dispatch/auto-plan/runs`, { params: { limit } })
    return res.data?.items ?? []
  }, [])
}

function clean<T extends object>(obj: T): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null && v !== '') out[k] = v
  }
  return out
}
